/**
 * The once-shown lapse banner (PPL-350, contacts list).
 *
 * It answers the contradiction a lapsed subscriber opens the page to: the slot
 * counter reads full while they plainly have forty ppl. Unexplained, that reads
 * as the app having lost their data — the one impression this tier cannot
 * afford, because nothing was lost. So the headline does that work before
 * anything else: **nothing was deleted.**
 *
 * Shown while the user doc carries `lapsedAt` (stamped server-side on a real
 * downgrade) and no `lapseBannerSeenAt`. A banner that returns every visit is a
 * nag, and this one lands at the worst possible moment to be nagged.
 *
 * Numbers are served, and each clause drops on its own when its number is not
 * available rather than printing a default that may be wrong for this cohort.
 */

import React, { useEffect } from 'react';
import { X } from 'react-feather';
import { Moon } from './glyphs';
import { RT } from './tokens';
import { capture } from '../../services/posthog';
import { MonetizationEvents, PaywallSource } from '../../services/monetizationEvents';
import type { RememberQuota } from '../../services/rememberService';

interface Props {
  quota: RememberQuota | null;
  onRestart: () => void;
  onChoose: () => void;
  onDismiss: () => void;
}

/**
 * Fired once per app session, not once per mount (PPL-351).
 *
 * Two reasons it lives out here rather than in a ref. A ref resets on every
 * mount, and this screen unmounts on every navigation to a contact and back — so
 * a lapsed user browsing five people would emit five views while the at-limit
 * sheet emits one per actual open, and the two surfaces stop being comparable,
 * which is the one comparison this instrumentation exists to make. And firing
 * from the COMPONENT rather than the hook is what makes it a sighting: the hook
 * only knows the flag, and the flag is true for a lapsed user sitting on a
 * different tab who never sees this.
 */
let sessionViewLogged = false;

export default function LapseBanner({ quota, onRestart, onChoose, onDismiss }: Props) {
  useEffect(() => {
    if (sessionViewLogged) return;
    sessionViewLogged = true;
    capture(MonetizationEvents.PAYWALL_VIEWED, {
      source: PaywallSource.LAPSE_BANNER,
      referrer_source: null,
      gate: null,
      tier_shown: 'premium',
      is_trialing: false,
    });
  }, []);

  const limit = quota?.limit ?? null;
  const days = quota?.memoryWindowDays ?? null;
  const lead = limit === null
    ? "f8 remembers the ppl you've seen most recently"
    : `f8 remembers the ${limit} ppl you've seen most recently`;
  const window = days === null ? '' : `, remembering the last ${days} days`;

  return (
    <div style={{
      borderRadius: 18, border: `1px solid ${RT.goldTintBorder}`, background: RT.goldTint,
      padding: 14, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 12, flexShrink: 0,
          background: 'rgba(251,191,36,0.10)', border: `1px solid ${RT.goldTintBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Moon size={15} color={RT.gold} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
            your premium ended — nothing was deleted
          </div>
          <div style={{ fontSize: 11.5, color: RT.muted, marginTop: 3, lineHeight: 1.5 }}>
            {lead}{window}. everything else — and everything older — is archived and waiting.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="dismiss"
          style={{
            appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
            padding: 4, marginTop: -2, color: RT.dimSolid, display: 'inline-flex',
          }}
        >
          <X size={13} />
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11 }}>
        <button
          type="button"
          onClick={onRestart}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
            padding: '8px 14px', borderRadius: 999, border: '1px solid transparent',
            background: 'linear-gradient(135deg, #d946ef, #7c3aed)', color: '#fff',
            fontSize: 12.5, fontWeight: 600, boxShadow: '0 6px 18px rgba(217,70,239,0.28)',
          }}
        >
          restart premium
        </button>
        <button
          type="button"
          onClick={onChoose}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
            padding: '8px 12px', borderRadius: 999, border: '1px solid transparent',
            background: 'transparent', color: RT.dim, fontSize: 12.5, fontWeight: 600,
          }}
        >
          choose my {limit ?? 'ppl'}
        </button>
      </div>
    </div>
  );
}
